import React, {useState} from "react";
import {Modal, Popconfirm, Tooltip, Badge} from "antd";
import {
    DeleteOutlined,
    EditOutlined,
    UploadOutlined,
    BellOutlined,
    CalculatorOutlined,
    FileSearchOutlined,
    TransactionOutlined
} from "@ant-design/icons";
import {useTranslation} from "react-i18next";
import moment from "moment";
import {useStore} from "../../../hooks/useStore";
import DataTablePropTypes from "./ComponentPropTypes";
import {TableContainer, ActionButtons, PaymentPeriodActionButton} from "./ComponentStyles";
import UpdateModal from "./UpdateModal/UpdateModal";
import ExcelUploadModal from "./ExcelUploadModal/ExcelUploadModal";
import {EmptyMessage} from "../../_defaultComponents/EmptyMessage/EmptyMessage";
import {PAYMENT_PERIOD_BTN} from "../../../constants/constants";

const DataTable = ({
    data,
    loading,
    setReloadData,
    currentPage,
    setCurrentPage,
    dataCount,
    selectedItem,
    setSelectedItem,
    showExcelUploadModal,
    setShowExcelUploadModal,
    setRecall
}: DataTablePropTypes) => {
    const {t} = useTranslation(["paymentManagement"]);
    const stores = useStore();
    const [deleteConfirmLoading, setDeleteConfirmLoading] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [notifyLoading, setNotifyLoading] = useState(false);
    const [notifyTestLoading, setNotifyTestLoading] = useState(false);
    const [showUpdateModal, setShowUpdateModal] = useState(false);
    const [calculateLoading, setCalculateLoading] = useState(false);
    const [processLoading, setProcessLoading] = useState(false);

    const closeUpdateModal = (reloadData = false) => {
        if (reloadData) setReloadData();
        setShowUpdateModal(false);
        setSelectedItem();
    };
    const closeExcelUploadModal = (reloadData = false) => {
        setRecall(prev => !prev);
        if (reloadData) setReloadData();
        setShowExcelUploadModal(false);
        setSelectedItem();
    };

    const deleteAction = async id => {
        setDeleteLoading(true);
        await stores.paymentPeriodStore.deletePaymentPeriod({_id: id});
        setReloadData();
        setDeleteLoading(false);
    };

    const calculateAction = async periodId => {
        setCalculateLoading(periodId);
        await stores.paymentPeriodStore.calculateAndProcess({
            action: "calculate",
            periodId: periodId
        });
        setReloadData();
        setCalculateLoading(false);
    };

    const processAction = async periodId => {
        setProcessLoading(periodId);
        await stores.paymentPeriodStore.calculateAndProcess({
            action: "process",
            periodId: periodId
        });
        setReloadData();
        setProcessLoading(false);
    };

    const notifyAction = async id => {
        setNotifyLoading(id);
        await stores.paymentPeriodStore.sentNotificationPaymentPeriod({
            _id: id,
            isTestNotification: false
        });
        setReloadData();
        setNotifyLoading(false);
    };

    const notifyTestAction = async id => {
        setNotifyTestLoading(id);
        await stores.paymentPeriodStore.sentNotificationPaymentPeriod({
            _id: id,
            isTestNotification: true
        });
        setReloadData();
        setNotifyTestLoading(false);
    };

    const renderActionButtons = (id, item) => {
        const canUpdate = true;
        const isCalculated =
            item.calculatedCouriersPaymentPeriod > 0 || item.processedCouriersPaymentPeriod > 0;
        const notAutoButtons = item.type !== "auto";

        const permisions = {
            calculate: canUpdate,
            detail: canUpdate && isCalculated,
            upload: canUpdate,
            update: canUpdate && notAutoButtons,
            delete: canUpdate,
            sendNotification: canUpdate && notAutoButtons,
            transferToWallet: canUpdate
        };

        return (
            <ActionButtons>
                {permisions.calculate && item.type === "auto" && (
                    <Tooltip title={t("actions.calculate", {ns: "common"})}>
                        <PaymentPeriodActionButton
                            {...PAYMENT_PERIOD_BTN.calculate}
                            icon={<CalculatorOutlined />}
                            data-testid="calculateButton"
                            loading={calculateLoading === item._id}
                            onClick={() =>
                                Modal.confirm({
                                    title: t("modal.calculatePaymentConfirm", {ns: "common"}),
                                    okText: t("modal.yesButton", {ns: "common"}),
                                    cancelText: t("modal.noButton", {ns: "common"}),
                                    onOk: () => calculateAction(item._id)
                                })
                            }
                        ></PaymentPeriodActionButton>
                    </Tooltip>
                )}
                {permisions.transferToWallet && item.type === "auto" && (
                    <Tooltip title={t("actions.transferToWallet", {ns: "common"})}>
                        <PaymentPeriodActionButton
                            {...PAYMENT_PERIOD_BTN.transferToWallet}
                            icon={<TransactionOutlined />}
                            data-testid="transferToWalletButton"
                            loading={processLoading === item._id}
                            onClick={() => {
                                if (!item.canProcessed) return;
                                Modal.confirm({
                                    title: t("modal.transferToWalletConfirm", {ns: "common"}),
                                    okText: t("modal.yesButton", {ns: "common"}),
                                    cancelText: t("modal.noButton", {ns: "common"}),
                                    onOk: () => processAction(item._id)
                                });
                            }}
                            disabled={!item.canProcessed}
                        ></PaymentPeriodActionButton>
                    </Tooltip>
                )}
                {item.type === "auto" && (
                    <Tooltip title={t("actions.detail", {ns: "common"})}>
                        <PaymentPeriodActionButton
                            {...PAYMENT_PERIOD_BTN.detail}
                            icon={<FileSearchOutlined />}
                            data-testid="detailButton"
                            disabled={!permisions.detail}
                            onClick={() => {
                                if (permisions.detail) {
                                    window.open(`/credit-logs?periodId=${item._id}`);
                                }
                            }}
                        ></PaymentPeriodActionButton>
                    </Tooltip>
                )}
                {permisions.upload && (
                    <Tooltip title={t("actions.upload", {ns: "common"})}>
                        <PaymentPeriodActionButton
                            {...PAYMENT_PERIOD_BTN.importExcel}
                            icon={<UploadOutlined />}
                            data-testid="importFromExcelButton"
                            onClick={() => {
                                setSelectedItem(item);
                                setShowExcelUploadModal(true);
                            }}
                        ></PaymentPeriodActionButton>
                    </Tooltip>
                )}
                {permisions.sendNotification && (
                    <Tooltip title={t("actions.notify", {ns: "common"})}>
                        <Popconfirm
                            title={t("modal.confirmTitle", {ns: "common"})}
                            open={open}
                            onConfirm={async () => {
                                await notifyAction(item._id);
                            }}
                            onCancel={() => {}}
                            okText={t("modal.yesButton", {ns: "common"})}
                            cancelText={t("modal.noButton", {ns: "common"})}
                        >
                            <PaymentPeriodActionButton
                                {...PAYMENT_PERIOD_BTN.notify}
                                loading={notifyLoading === id}
                                data-testid="notifyButton"
                                icon={<BellOutlined />}
                            ></PaymentPeriodActionButton>
                        </Popconfirm>
                    </Tooltip>
                )}
                {permisions.sendNotification && (
                    <Tooltip title={t("actions.notifyTest", {ns: "common"})}>
                        <Popconfirm
                            title={t("modal.confirmTitle", {ns: "common"})}
                            open={open}
                            onConfirm={async () => {
                                await notifyTestAction(item._id);
                            }}
                            onCancel={() => {}}
                            okText={t("modal.yesButton", {ns: "common"})}
                            cancelText={t("modal.noButton", {ns: "common"})}
                        >
                            <Badge dot={true} offset={[-10, 5]} color={"blue"}>
                                <PaymentPeriodActionButton
                                    {...PAYMENT_PERIOD_BTN.notifyTest}
                                    loading={notifyTestLoading === id}
                                    data-testid="notifyTestButton"
                                    icon={<BellOutlined />}
                                ></PaymentPeriodActionButton>
                            </Badge>
                        </Popconfirm>
                    </Tooltip>
                )}

                {permisions.update && (
                    <Tooltip title={t("actions.update", {ns: "common"})}>
                        <PaymentPeriodActionButton
                            {...PAYMENT_PERIOD_BTN.update}
                            data-testid="updateButton"
                            disabled={deleteConfirmLoading}
                            loading={deleteConfirmLoading === id}
                            onClick={() => {
                                setSelectedItem(item);
                                setShowUpdateModal(true);
                            }}
                            icon={<EditOutlined />}
                        ></PaymentPeriodActionButton>
                    </Tooltip>
                )}

                {permisions.delete && (
                    <Tooltip title={t("actions.delete", {ns: "common"})}>
                        <Popconfirm
                            title={t("modal.confirmTitle", {ns: "common"})}
                            open={open}
                            onConfirm={async () => {
                                await deleteAction(item._id);
                            }}
                            onCancel={() => {}}
                            okText={t("modal.yesButton", {ns: "common"})}
                            cancelText={t("modal.noButton", {ns: "common"})}
                        >
                            <PaymentPeriodActionButton
                                {...PAYMENT_PERIOD_BTN.delete}
                                loading={deleteLoading && id}
                                data-testid="deleteButton"
                                icon={<DeleteOutlined />}
                            ></PaymentPeriodActionButton>
                        </Popconfirm>
                    </Tooltip>
                )}
            </ActionButtons>
        );
    };

    return (
        <>
            <UpdateModal
                visibility={Boolean(selectedItem?._id) && showUpdateModal}
                onCancel={closeUpdateModal}
                id={selectedItem?._id}
                startDate={moment(selectedItem?.startDate)}
                endDate={moment(selectedItem?.endDate)}
                name={selectedItem?.name}
                invoiceDate={moment(selectedItem?.invoiceDate)}
                paymentDate={moment(selectedItem?.paymentDate)}
                periodType={selectedItem?.periodType || "default"}
            />
            <ExcelUploadModal
                visibility={Boolean(selectedItem?._id) && showExcelUploadModal}
                onClose={closeExcelUploadModal}
                id={selectedItem?._id}
                name={selectedItem?.name}
            />
            <TableContainer
                pagination={{
                    total: dataCount,
                    current: currentPage,
                    onChange: async page => {
                        setCurrentPage(page);
                    },
                    hideOnSinglePage: true,
                    showSizeChanger: false
                }}
                locale={{
                    emptyText: <EmptyMessage />
                }}
                rowKey="_id"
                dataSource={data}
                data-testid="paymentPeriodTable"
                loading={loading || calculateLoading || processLoading}
                display={data.length > 0 ? "grid" : "flex"}
                dataTitles={[
                    "Dönem (Gün Ay Yıl)",
                    "Kurye Sayısı",
                    "Hesaplanmayan Kurye Sayısı",
                    "Hesaplanan Kurye Sayısı",
                    "Aktarılan Kurye Sayısı",
                    "Gönderilen",
                    "İletilen",
                    "Görülen",
                    t("actions.action", {ns: "common"})
                ]}
            >
                <TableContainer.Column
                    dataIndex="name"
                    title={"Dönem (Gün Ay Yıl)"}
                    //title={t("fields.date", {ns: "common"})}
                    render={name => (
                        <span style={{textAlign: "center"}} data-testid="name">
                            {!name ? "-" : name}
                        </span>
                    )}
                />
                <TableContainer.Column
                    dataIndex="type"
                    title={"Hesaplama Tipi"}
                    //title={t("fields.date", {ns: "common"})}
                    render={type => (
                        <span style={{textAlign: "center"}} data-testid="type">
                            {!type ? "-" : type === "auto" ? "Otomatik" : "Excel"}
                        </span>
                    )}
                />
                <TableContainer.Column
                    dataIndex="notCalculatedCouriersPaymentPeriod"
                    title={"Hesaplanmayan Kurye Sayısı"}
                    align="center"
                    render={(notCalculatedCouriersPaymentPeriod, record) => (
                        <span>
                            {record?.type === "auto"
                                ? notCalculatedCouriersPaymentPeriod || 0
                                : "-"}
                        </span>
                    )}
                />
                <TableContainer.Column
                    dataIndex="calculatedCouriersPaymentPeriod"
                    title={"Hesaplanan Kurye Sayısı"}
                    align="center"
                    //title={t("fields.date", {ns: "common"})}
                    render={(calculatedCouriersPaymentPeriod, record) => (
                        <span>
                            {record?.type === "auto" ? calculatedCouriersPaymentPeriod || 0 : "-"}
                        </span>
                    )}
                />
                <TableContainer.Column
                    dataIndex="processedCouriersPaymentPeriod"
                    title={"Aktarılan Kurye Sayısı"}
                    align="center"
                    render={(processedCouriersPaymentPeriod, record) => (
                        <span>
                            {record?.type === "auto" ? processedCouriersPaymentPeriod || 0 : "-"}
                        </span>
                    )}
                />
                <TableContainer.Column
                    dataIndex="informed"
                    title={"Gönderilen"}
                    align="center"
                    //title={t("fields.date", {ns: "common"})}
                    render={informed => (
                        <span data-testid="informed">
                            {!informed && isNaN(informed) ? "-" : informed}
                        </span>
                    )}
                />
                <TableContainer.Column
                    dataIndex="received"
                    title={"İletilen"}
                    align="center"
                    //title={t("fields.date", {ns: "common"})}
                    render={received => (
                        <span data-testid="received">
                            {!received && isNaN(received) ? "-" : received}
                        </span>
                    )}
                />
                <TableContainer.Column
                    dataIndex="seen"
                    title={"Görülen"}
                    align="center"
                    //title={t("fields.date", {ns: "common"})}
                    render={seen => (
                        <span data-testid="seen">{!seen && isNaN(seen) ? "-" : seen}</span>
                    )}
                />

                <TableContainer.Column
                    align="right"
                    title={t("actions.action", {ns: "common"})}
                    dataIndex="_id"
                    render={renderActionButtons}
                />
            </TableContainer>
        </>
    );
};

export default DataTable;
